import { Component, computed, ElementRef, input, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from '@shared/services/toastr.service';
import { copyObject } from '@shared/utils/data-utils';
import { isValidPath } from '@shared/validators/validators';
import { IpWithComment, OrganizationSiteService, UrlException } from '@shared/services/organization-site.service';
import { Site } from '@shared/models/site';
import { MakeOptional } from '@shared/utils/type-utils';
import _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { NgIf, NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DisableOnClickDirective } from '@shared/directives/disable-button/disable-on-click.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { IpListComponent, IpListService } from './ip-list/ip-list.component';
import { FeatureFlagDirective } from '@shared/utils/feature-flag.directive';

@Component({
  selector: 'app-exceptions',
  templateUrl: './exceptions.component.html',
  styleUrls: ['./exceptions.component.scss', '../add-site.component.scss'],
  imports: [IpListComponent, NgStyle, NgIf, FormsModule, DisableOnClickDirective, TranslatePipe, FeatureFlagDirective],
})
export class ExceptionsComponent implements OnInit {
  site = input.required<Site>();

  urlExceptions: UrlExceptionVM[] = [];
  tmpUrlException: UrlExceptionVM;

  @ViewChild('urlExceptionElement') urlExceptionElement: ElementRef;

  isLoading = new BehaviorSubject<boolean>(false);
  isInvalidPath: boolean = false;

  whitelistedIpsService = computed(() => new WhitelistedIpsService(this.siteApi, this.site().domainName));
  blacklistedIpsService = computed(() => new BlacklistedIpsService(this.siteApi, this.site().domainName));
  trustedIpsService = computed(() => new TrustedIpsService(this.siteApi, this.site().domainName));

  constructor(
    private siteApi: OrganizationSiteService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.loadUrlExceptions();
  }

  loadUrlExceptions() {
    this.urlExceptions = [];
    this.siteApi.getUrlExceptions(this.site().domainName).subscribe({
      next: (res: any[]) => {
        this.urlExceptions = res;
      },
      error: (error: any) => {
        this.toastr.error('Failed to load URL exceptions');
        console.error('Error : ', error);
      },
    });
  }

  addUrlException() {
    this.urlExceptions.push({ path: '/', comment: '' });
    setTimeout(() => this.urlExceptionElement.nativeElement.focus(), 1);
  }

  isCreatingUrlException() {
    return this.urlExceptions.some((rule) => isNew(rule));
  }

  createUrlException(urlException: UrlExceptionVM) {
    const path = urlException.path.trim();
    if (this.urlExceptions.find((u) => u.path == path && !isNew(u))) {
      return this.toastr.error('AlreadyExists');
    }

    this.isLoading.next(true);
    this.siteApi.addUrlException(this.site().domainName, { path: path, comment: urlException.comment }).subscribe({
      next: (res) => {
        this.isLoading.next(false);
        this.urlExceptions.splice(
          this.urlExceptions.findIndex((u) => isNew(u)),
          1,
        );
        this.urlExceptions.push(res);
        this.toastr.success('EnregistrementReussi');
        this.addUrlException();
      },
      error: () => {
        this.isLoading.next(false);
        this.toastr.error('EnregistrementEchoue');
      },
    });
  }

  deleteUrlException(urlException: UrlExceptionVM) {
    if (urlException.editing) {
      Object.assign(urlException, this.tmpUrlException, { editing: false });
      return;
    } else if (isNew(urlException)) {
      this.urlExceptions.splice(
        this.urlExceptions.findIndex((u) => isNew(u)),
        1,
      );
    }

    if (!isNew(urlException)) {
      this.siteApi.deleteUrlException(this.site().domainName, urlException.id).subscribe({
        next: () => {
          this.loadUrlExceptions();
          this.toastr.success();
        },
        error: () => {
          this.toastr.error();
        },
      });
    } else {
      this.urlExceptions.filter((r) => r.id);
    }
  }

  startUrlExceptionEditing(urlException: UrlExceptionVM) {
    this.tmpUrlException = copyObject(urlException); // deep copy in order to restore if cancelling editing
    urlException.editing = true;
    setTimeout(() => this.urlExceptionElement.nativeElement.focus(), 1);
  }

  validateUrlExceptionEditing(urlException: UrlExceptionVM) {
    if (isNew(urlException)) throw new Error('urlException.id should be present');
    if (urlException.path) {
      urlException.editing = false;

      this.isLoading.next(true);
      this.siteApi.updateUrlException(this.site().domainName, <UrlException>_.omit(urlException, 'editing')).subscribe({
        next: () => {
          this.loadUrlExceptions();
          this.toastr.success();
          this.isLoading.next(true);
        },
        error: () => {
          this.toastr.error();
          this.isLoading.next(true);
        },
      });
    }
  }

  isEditingUrlException() {
    return this.urlExceptions.some((urlException) => urlException.editing);
  }

  handleEnterUrlException = (event: KeyboardEvent, urlException: UrlExceptionVM) => {
    if (event.key === 'Enter' && !event.shiftKey && !this.isLoading.getValue()) {
      this.isLoading.next(true);
      urlException.editing ? this.validateUrlExceptionEditing(urlException) : this.createUrlException(urlException);
      event.preventDefault();
    } else if (event.key === 'Escape') {
      this.deleteUrlException(urlException);
    }
  };

  id(index, item) {
    return item.id ?? 'new';
  }

  checkPath(input: string) {
    this.isInvalidPath = !isValidPath(input);
  }
}

function isNew({ id }: { id?: any }) {
  return id === undefined || id === null;
}

type UrlExceptionVM = MakeOptional<UrlException, 'id'> & {
  editing?: boolean;
};

class WhitelistedIpsService implements IpListService {
  constructor(
    private readonly siteApi: OrganizationSiteService,
    private readonly domainName: string,
  ) {}

  getIps(): Observable<IpWithComment[]> {
    return this.siteApi.getIpExceptions(this.domainName);
  }

  setIps(ipExceptions: IpWithComment[]): Observable<IpWithComment[]> {
    return this.siteApi.setIpExceptions(this.domainName, ipExceptions);
  }
}

class BlacklistedIpsService implements IpListService {
  constructor(
    private readonly siteApi: OrganizationSiteService,
    private readonly domainName: string,
  ) {}

  getIps(): Observable<IpWithComment[]> {
    return this.siteApi.getBlacklistedIps(this.domainName);
  }

  setIps(blacklistedIps: IpWithComment[]): Observable<IpWithComment[]> {
    return this.siteApi.setBlacklistedIps(this.domainName, blacklistedIps);
  }
}
class TrustedIpsService implements IpListService {
  constructor(
    private readonly siteApi: OrganizationSiteService,
    private readonly domainName: string,
  ) {}

  getIps(): Observable<IpWithComment[]> {
    return this.siteApi.getTrustedIps(this.domainName);
  }

  setIps(trustedIps: IpWithComment[]): Observable<IpWithComment[]> {
    return this.siteApi.setTrustedIps(this.domainName, trustedIps);
  }
}
